import { GiftDeliveryForAdmin } from '@shared/api/giftDelivery/giftDelivery.interface';
import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { GiftSupplySettlement } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { getLineItemName, splitLineSurcharge } from './settlement.utils';

// ─────────────────────────────────────────────
// 업체정산 상세내역 엑셀 다운로드
// 정산 문서의 라인(상세내역) 1건당 1행으로 내보낸다.
// ─────────────────────────────────────────────

const NUM_FMT = '#,##0';
const MONEY_LABELS = ['도매단가', '소량추가', '택배비', '정산금액'];

const fmtD = (d?: Date | string): string => {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
};

/** 한글 2.2칸/영문 1칸 기준으로 컬럼 폭 자동 조정 */
function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 6;
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v != null) {
        let w = 0;
        for (const ch of String(cell.v)) w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
        if (w > max) max = w;
      }
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 3, 45) });
  }
  ws['!cols'] = cols;
}

/** 헤더 라벨이 금액 컬럼이면 #,##0 숫자 포맷 적용 */
function applyMoneyFormat(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const moneyCols: number[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    const hdr = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (hdr && MONEY_LABELS.includes(String(hdr.v))) moneyCols.push(C);
  }
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    for (const C of moneyCols) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && typeof cell.v === 'number') cell.z = NUM_FMT;
    }
  }
}

/**
 * 정산 문서 1건의 상세내역(라인)을 엑셀로 다운로드한다.
 * @param s 정산 문서
 * @param mySettlementInfo 공급사 정산정보 (택배비/소량추가 분해용)
 */
export function exportSupplySettlementDetailExcel(
  s: GiftSupplySettlement,
  mySettlementInfo?: SettlementInfo,
) {
  let sumExtra = 0;
  let sumQty = 0;
  let sumCourier = 0;
  let sumLineAmount = 0;

  const rows: Record<string, unknown>[] = s.lines.map((line, idx) => {
    const { extraUnitAmount, courierFee } = splitLineSurcharge(
      line.quantity,
      line.surcharge,
      mySettlementInfo,
    );
    const delivery: GiftDeliveryForAdmin | null =
      typeof line.giftDelivery === 'object' ? line.giftDelivery : null;
    const courier = courierFee > 0 ? courierFee : 0;

    sumExtra += extraUnitAmount;
    sumQty += line.quantity;
    sumCourier += courier;
    sumLineAmount += line.lineAmount;

    return {
      '#': idx + 1,
      상품: getLineItemName(line),
      배송요청일: fmtD(delivery?.recipient?.submittedAt),
      배송완료일: fmtD(delivery?.shipping?.deliveredAt),
      택배사: delivery?.shipping?.carrier ?? '',
      송장번호: delivery?.shipping?.trackingNumber ?? '',
      도매단가: line.unitAmount,
      소량추가: extraUnitAmount,
      수량: line.quantity,
      택배비: courier,
      정산금액: line.lineAmount,
    };
  });

  // 합계 행 (도매단가는 개당 단가라 합산 제외). 색 미지원 라이브러리라 텍스트로 강조.
  rows.push({
    '#': '',
    상품: '■ 합계',
    배송요청일: '',
    배송완료일: '',
    택배사: '',
    송장번호: '',
    도매단가: '',
    소량추가: sumExtra,
    수량: sumQty,
    택배비: sumCourier,
    정산금액: sumLineAmount,
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws);
  applyMoneyFormat(ws);
  XLSX.utils.book_append_sheet(wb, ws, '정산상세');

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([buf], { type: 'application/octet-stream' }),
    `정산상세_${s.year}년${s.month}월.xlsx`,
  );
}
